import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  effect,
  ChangeDetectionStrategy
} from '@angular/core';

import { Category } from '../../../models/category.model';
import { CategoryService } from '../../../services/category.service';
import { OrganizationService } from '../../../core/services/organization.service';
import { ErrorHandlingService } from '../../../shared/services/error-handling.service';
import { ToastService } from '../../../shared/services/toast.service';
import { CategoryIconComponent } from '../../../shared/components/category-icon/category-icon.component';
import { CategoryEnvBadgeComponent } from '../../../shared/components/category-env-badge/category-env-badge.component';

@Component({
  selector: 'app-category-selector',
  standalone: true,
  imports: [CategoryIconComponent, CategoryEnvBadgeComponent],
  templateUrl: './category-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./category-selector.component.css']
})
export class CategorySelectorComponent implements OnInit {
  @Input() selectedCategoryId: string = '';
  @Input() loading = false;
  @Output() categorySelected = new EventEmitter<Category | null>();

  categories: Category[] = [];
  loadingCategories = true; // Start as true to prevent flash of error state

  /** Whether the custom dropdown panel is open */
  isOpen = false;
  /** Index of the keyboard-highlighted option while the panel is open */
  activeIndex = -1;

  // Instance-scoped id prefix so listbox/option ids never collide when the
  // component is rendered more than once on a page.
  private static nextUid = 0;
  readonly uid = `catsel-${CategorySelectorComponent.nextUid++}`;
  readonly listboxId = `${this.uid}-listbox`;

  optionId(index: number): string {
    return `${this.uid}-option-${index}`;
  }

  private initialized = false;
  // Last orgId we kicked off a load for. Prevents the effect from re-loading
  // when the signal settles to the same value shortly after init (auth resolving).
  private lastLoadedOrgId: string | null = null;

  constructor(
    private categoryService: CategoryService,
    private organizationService: OrganizationService,
    private errorHandlingService: ErrorHandlingService,
    private toastService: ToastService,
    private elementRef: ElementRef<HTMLElement>
  ) {
    effect(() => {
      const orgId = this.organizationService.selectedOrganizationId();
      if (!this.initialized) return;
      if (orgId === this.lastLoadedOrgId) return;
      this.lastLoadedOrgId = orgId;
      // Pass the snapshot so dedup tracking and the HTTP request stay in sync
      // even if the signal changes again before the call hits the network.
      this.loadCategories(orgId);
    });
  }

  ngOnInit(): void {
    // Mark initialized BEFORE loading so the effect can respond to any signal
    // change that arrives during/after the initial load — otherwise an
    // organization change that lands while initialized is still false would
    // never be picked up.
    this.initialized = true;
    const orgId = this.organizationService.selectedOrganizationId();
    this.lastLoadedOrgId = orgId;
    this.loadCategories(orgId);
  }

  /** The currently selected category (resolved from selectedCategoryId). */
  get selectedCategory(): Category | null {
    return this.categories.find(category => category.id === this.selectedCategoryId) || null;
  }

  loadCategories(orgId: string | null): void {
    this.loadingCategories = true;
    this.isOpen = false;

    // Route is guarded by authGuard, so a user is always present here.
    const organizationId = orgId ?? undefined;

    this.categoryService.getCategories(organizationId).subscribe({
      next: (categories) => {
        this.categories = categories;
        this.loadingCategories = false;
        if (categories.length === 0) {
          // Emit null explicitly when no categories available
          this.categorySelected.emit(null);
        } else {
          this.categorySelected.emit(
            this.categories.find(category => category.id === this.selectedCategoryId) || categories[0]
          );
        }
      },
      error: (err) => {
        this.loadingCategories = false;
        this.toastService.error(this.errorHandlingService.getGeneralErrorMessage(err));
      }
    });
  }

  toggleDropdown(): void {
    if (this.loading || this.loadingCategories) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      const selectedIndex = this.categories.findIndex(
        category => category.id === this.selectedCategoryId
      );
      this.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
    }
  }

  selectCategory(category: Category): void {
    this.isOpen = false;
    this.categorySelected.emit(category);
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.isOpen = false;
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) {
          this.toggleDropdown();
        } else if (this.activeIndex < this.categories.length - 1) {
          this.activeIndex++;
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen && this.activeIndex > 0) {
          this.activeIndex--;
        }
        break;
      case 'Enter':
      case ' ':
        if (this.isOpen) {
          event.preventDefault();
          const category = this.categories[this.activeIndex];
          if (category) {
            this.selectCategory(category);
          }
        }
        // When closed, the default button click behavior opens the panel.
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }
}
